/**
 * What a review item asks of the reviewer, and therefore which controls it
 * renders.
 *
 * The mapping kind -> response mode is 1:1 and lives here rather than in a
 * stored column: a derivation that is also persisted is a drift risk, and the
 * DB already pins the pair it actually needs (approval_decisions carries
 * item_kind behind a composite FK, so its verdict CHECK cannot be fooled).
 */
export const APPROVAL_ITEM_KINDS = [
  'post',
  'asset',
  'plan',
  'decision',
  'input',
  'context',
  /**
   * A live web page on the CLIENT'S OWN site, needing their decision — a
   * staged WordPress page, a service page, a landing page. Mora did not write
   * it and does not host it.
   *
   * 🔴 A PAGE NEVER CARRIES A `project_task_id`, AND THE DATABASE ENFORCES
   * THAT RATHER THAN THIS COMMENT. `approval_items_pointer_shape` allows that
   * column only for `post | asset | plan | context`; `page` is deliberately
   * absent, so a page item is structurally standalone. That matters twice:
   * these pages genuinely have no plan row behind them (checked against ITC
   * Tax — PTO/CRE/Renewables are service lines, matching none of the 72
   * editorial items), and `dedupeByUnderlyingObject` keys on
   * `task:{project_task_id}` and keeps only ONE row per key, so a page sharing
   * a task with an article would silently swallow one of the two decisions.
   */
  'page',
] as const;
export type ApprovalItemKind = (typeof APPROVAL_ITEM_KINDS)[number];

export const APPROVAL_VERDICTS = ['approved', 'changes_requested', 'declined', 'answered'] as const;
export type ApprovalVerdict = (typeof APPROVAL_VERDICTS)[number];

/**
 * `none` is not a missing value. A plan commitment with nothing to decide
 * ("follow up with people met") still belongs on the page as context, and is
 * deliberately excluded from the progress count — a count that includes rows
 * the reviewer cannot act on tells them the job is bigger than it is.
 */
export type ApprovalResponseMode = 'judgment' | 'decision' | 'input' | 'none';

const RESPONSE_MODE_BY_KIND: Record<ApprovalItemKind, ApprovalResponseMode> = {
  post: 'judgment',
  asset: 'judgment',
  plan: 'judgment',
  decision: 'decision',
  input: 'input',
  context: 'none',
  /*
   * Judgment, NOT `decision`, and the difference is a capability rather than
   * wording. `decision` mode offers approved|declined — "should we do this at
   * all" — which would let a client approve a staged page or kill it and never
   * be able to ask for a fix. "This is good except the third paragraph" is the
   * single most likely thing a reviewer wants to say about a page.
   */
  page: 'judgment',
};

const VERDICTS_BY_MODE: Record<ApprovalResponseMode, readonly ApprovalVerdict[]> = {
  judgment: ['approved', 'changes_requested'],
  decision: ['approved', 'declined'],
  input: ['answered'],
  none: [],
};

export function responseModeForKind(kind: string): ApprovalResponseMode {
  return RESPONSE_MODE_BY_KIND[kind as ApprovalItemKind] ?? 'none';
}

export function isActionableKind(kind: string): boolean {
  return responseModeForKind(kind) !== 'none';
}

/**
 * The client-side half of `approval_decisions_verdict_matches_kind`. The DB
 * constraint is the one that must hold; this exists so a bad pairing is caught
 * before it becomes a 23514 the reviewer sees as "something went wrong".
 */
export function isVerdictValidForKind(kind: string, verdict: string): boolean {
  const allowed = VERDICTS_BY_MODE[responseModeForKind(kind)];
  return (allowed as readonly string[]).includes(verdict);
}

/**
 * A blocking verdict must carry a reason, and an answer must carry an answer.
 * Mirrors approval_decisions_reason_required / _answer_required so the UI can
 * disable the button rather than let the write fail. Returns a reason string
 * when invalid so callers never have to re-derive the message.
 */
export function validateDecisionInput(input: {
  kind: string;
  verdict: string;
  comment?: string | null;
  responseText?: string | null;
  responseMediaId?: string | null;
}): { status: 'ok' } | { status: 'invalid'; reason: string } {
  if (!isVerdictValidForKind(input.kind, input.verdict)) {
    return { status: 'invalid', reason: 'That response does not match what this item asks for.' };
  }
  if (input.verdict === 'changes_requested' && !input.comment?.trim()) {
    return { status: 'invalid', reason: 'Say what needs to change so it can actually be fixed.' };
  }
  if (input.verdict === 'answered' && !input.responseText?.trim() && !input.responseMediaId) {
    return { status: 'invalid', reason: 'Add a note or a photo before sending.' };
  }
  return { status: 'ok' };
}

/** Reviewer-facing wording. Words a non-marketer says out loud (spec D5). */
export const VERDICT_LABEL: Record<ApprovalVerdict, string> = {
  approved: 'Approved',
  changes_requested: 'Needs changes',
  declined: 'Skipped',
  answered: 'Answered',
};

export const KIND_LABEL: Record<ApprovalItemKind, string> = {
  post: 'Post',
  asset: 'Graphic',
  plan: 'Planned post',
  decision: 'Your call',
  input: 'Needs something from you',
  context: 'For your information',
  /*
   * OPERATOR wording. This map is not rendered on the Content Desk at all —
   * the client-facing card and panel label themselves from
   * DESK_CONTENT_TYPE_LABEL ('Web page'). Its one live reader is the
   * operator's send ledger, ApprovalDockBody.tsx:216.
   */
  page: 'Staged page',
};

/** The primary/secondary button wording, by mode. */
export const RESPONSE_ACTIONS: Record<
  Exclude<ApprovalResponseMode, 'none'>,
  {
    primary: { label: string; verdict: ApprovalVerdict };
    secondary?: { label: string; verdict: ApprovalVerdict };
  }
> = {
  judgment: {
    primary: { label: 'Approve', verdict: 'approved' },
    secondary: { label: 'Needs changes', verdict: 'changes_requested' },
  },
  decision: {
    primary: { label: 'Yes, do it', verdict: 'approved' },
    secondary: { label: 'No, skip it', verdict: 'declined' },
  },
  input: {
    primary: { label: 'Send', verdict: 'answered' },
  },
};
